import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Loader2, CheckCircle2, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const LS_KEY = 'activeGenReportId';

interface JobState {
  status: 'running' | 'done' | 'error' | 'cancelled';
  total: number;
  done: number;
  currentLabel: string | null;
}

/**
 * Globaler KI-Generierungs-Indikator (im Header, auf allen Seiten sichtbar).
 * Pollt den Sammel-Generierungs-Job des zuletzt gestarteten Berichts (localStorage
 * 'activeGenReportId') und zeigt den Fortschritt — auch wenn der Nutzer den Editor
 * verlassen hat. So sieht man jederzeit, dass die KI arbeitet.
 */
export function GenerationIndicator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reportId, setReportId] = useState<string | null>(null);
  const [job, setJob] = useState<JobState | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // localStorage beobachten (auch tab-übergreifend) + initial lesen.
  useEffect(() => {
    const read = () => setReportId(localStorage.getItem(LS_KEY));
    read();
    window.addEventListener('storage', read);
    // Eigener Tab ändert localStorage ohne 'storage'-Event → kurzes Polling der Marke.
    const i = setInterval(read, 1500);
    return () => { window.removeEventListener('storage', read); clearInterval(i); };
  }, []);

  // Job-Status pollen, solange ein activeGenReportId gesetzt ist.
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!reportId || !user) { setJob(null); return; }
    setDismissed(false);
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get<{ job: any }>(`/api/reports/${reportId}/generate-all/status`);
        if (cancelled) return;
        if (!res.job) { setJob(null); localStorage.removeItem(LS_KEY); return; }
        setJob({ status: res.job.status, total: res.job.total, done: res.job.done, currentLabel: res.job.currentLabel });
        // Fertig/abgebrochen → Marke entfernen (Badge bleibt bis zum Wegklicken sichtbar).
        if (res.job.status !== 'running') localStorage.removeItem(LS_KEY);
      } catch { /* weiter pollen */ }
    };
    void poll();
    pollRef.current = setInterval(poll, 2500);
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [reportId, user]);

  if (!job || dismissed) return null;

  const pct = job.total > 0 ? Math.round((job.done / job.total) * 100) : 0;
  const goToEditor = () => { if (reportId) navigate(`/editor/${reportId}`); };

  if (job.status === 'running') {
    return (
      <button
        onClick={goToEditor}
        title={`KI erstellt den Bericht — ${job.currentLabel || ''}`}
        className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        KI erstellt Bericht… {job.done}/{job.total}
        <span className="w-12 h-1.5 rounded-full bg-blue-200 dark:bg-blue-900 overflow-hidden">
          <span className="block h-full bg-blue-600" style={{ width: `${pct}%` }} />
        </span>
      </button>
    );
  }

  // done / error / cancelled — kurzer Hinweis mit Schließen.
  const isDone = job.status === 'done';
  return (
    <span className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
      isDone ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
             : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
    }`}>
      {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
      <button onClick={goToEditor} className="hover:underline">
        {isDone ? 'Bericht fertig — anzeigen' : 'Generierung abgebrochen'}
      </button>
      <button onClick={() => setDismissed(true)} aria-label="Ausblenden" className="ml-0.5 opacity-60 hover:opacity-100">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
